import { useState } from 'react';
import { Save, X } from 'lucide-react';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { blockTypeMeta, blockTypesForDiscipline } from '../../lib/blockTypes';
import type { TrainingBlock, BlockFormData, BlockType, Discipline } from '../../types';
import clsx from 'clsx';

interface BlockFormProps {
  dayId: string;
  parentBlockId?: string | null;
  discipline?: Discipline | null;
  initialData?: TrainingBlock;
  onSubmit: (data: BlockFormData) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

export function BlockForm({ dayId, parentBlockId, discipline, initialData, onSubmit, onCancel, isLoading }: BlockFormProps) {
  // Vocabulario de bloques según la disciplina del plan (universales + extras).
  const blockTypes = blockTypesForDiscipline(discipline).map((value) => ({
    value,
    ...blockTypeMeta[value],
  }));
  const [formData, setFormData] = useState<BlockFormData>({
    type: (initialData?.type as BlockType) || 'exercise',
    order: initialData?.order ?? 0,
    name: initialData?.name || '',
    notes: initialData?.notes || '',
    restSeconds: initialData?.restSeconds || undefined,
    dayId: dayId,
    parentBlockId: initialData?.parentBlockId ?? parentBlockId ?? null,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? (value ? parseInt(value) : undefined) : value,
    }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="label">Tipo de Bloque *</label>
        <div className="grid grid-cols-2 gap-2">
          {blockTypes.map(({ value, label, icon: Icon, description }) => (
            <button
              key={value}
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, type: value }))}
              className={clsx(
                'p-3 rounded-xl border-2 text-left transition-all',
                formData.type === value
                  ? 'border-primary-500 bg-primary-500/10'
                  : 'border-dark-600 bg-dark-800/50 hover:border-dark-500'
              )}
            >
              <div className="flex items-center gap-2 mb-1">
                <Icon className={clsx(
                  'w-4 h-4',
                  formData.type === value ? 'text-primary-400' : 'text-dark-400'
                )} />
                <span className={clsx(
                  'font-medium text-sm',
                  formData.type === value ? 'text-primary-400' : 'text-dark-200'
                )}>
                  {label}
                </span>
              </div>
              <p className="text-xs text-dark-400">{description}</p>
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="name" className="label">Nombre (opcional)</label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className="input"
            placeholder="Ej: Ejercicios Principales"
          />
        </div>
        <div>
          <label htmlFor="restSeconds" className="label">Descanso entre series (seg)</label>
          <input
            type="number"
            id="restSeconds"
            name="restSeconds"
            value={formData.restSeconds || ''}
            onChange={handleChange}
            min={0}
            className="input"
            placeholder="60"
          />
        </div>
      </div>

      <div>
        <label htmlFor="notes" className="label">Notas</label>
        <textarea
          id="notes"
          name="notes"
          value={formData.notes}
          onChange={handleChange}
          rows={2}
          className="input resize-none"
          placeholder="Instrucciones especiales..."
        />
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t border-dark-700">
        <button type="button" onClick={onCancel} className="btn-secondary">
          <X className="w-4 h-4" />
          Cancelar
        </button>
        <button type="submit" disabled={isLoading} className="btn-primary">
          {isLoading ? (
            <LoadingSpinner size="sm" />
          ) : (
            <>
              <Save className="w-4 h-4" />
              {initialData ? 'Guardar' : 'Crear Bloque'}
            </>
          )}
        </button>
      </div>
    </form>
  );
}

